import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from '@uikit/Form/Button/Button';
import { IMoreActions } from '@uikit/old/HeaderTemplate/HeaderTemplate.model';
import FiltersTooltipContentRaw, {
  FilterField,
} from '@uikit/old/HeaderTemplate/HeaderTemplateFilters/FiltersTooltipContentRaw/FiltersTooltipContentRaw';
import { SVGIcon, SvgIconType } from '@uikit/old/Icon/Icon';
import HeaderFiltersButtonWithTooltip from '@uikit/old/PageHeader/HeaderFiltersButtonWithTooltip/HeaderFiltersButtonWithTooltip';
import FiltersTooltipContentFooter from '@uikit/old/HeaderTemplate/HeaderTemplateFilters/FiltersTooltipContentFooter/FiltersTooltipContentFooter';
import Input from '@uikit/Form/Input/Input';
import MoreActionsButton from '@uikit/old/HeaderTemplate/MoreActionsButton/MoreActionsButton';

import { FilterFieldType } from '@ts/filterField.type';
import { GREY_COLOR, PRIMARY_COLOR } from '@constants/colors';
import useOutsideClick from '@hooks/useOutsideClick';
import useNonInitialEffect from '@hooks/useNonInitialEffect';
import debounce from '@utils/debounce';

import './HeaderActions.scss';

function HeaderActions({
  addNewBtn,
  secondaryBtn,
  selectedItems,
  handleRemoveItems,
  hasRemoveConfirmation,
  handleSearchValue,
  moreActions,
  initialFilterState,
  filterFields,
  appliedFilters,
  onApplyFilters,
  handleExportBtn,
  exportIsLoading,
  exportLoadingText,
  searchPlaceholder,
  controlledFilters,
  controlledSearchString,
  withRemoveFeature = true,
  showHandleExportButton = () => selectedItems?.length > 0,
  skipDayCheck,
  testIdPrefix,
  resetKey,
}: {
  addNewBtn?: {
    title: string;
    handleClick: () => void;
    isLoading?: boolean;
    isLoadingText?: string;
    /** data-testid for this button; wins over the `${testIdPrefix}-new` default */
    testId?: string;
  };
  /** Optional "additional"-styled action rendered right before `addNewBtn`. */
  secondaryBtn?: {
    title: string;
    handleClick: () => void;
    icon?: SvgIconType;
    isLoading?: boolean;
    isLoadingText?: string;
    /** data-testid for this button; wins over the `${testIdPrefix}-secondary` default */
    testId?: string;
  };
  selectedItems?: any[];
  handleRemoveItems?: () => void;
  hasRemoveConfirmation?: boolean;
  handleSearchValue?: (value: string) => void;
  moreActions?: IMoreActions[];
  initialFilterState?: any;
  filterFields?: FilterField[];
  appliedFilters?: FilterFieldType<any>[];
  onApplyFilters?: any;
  handleExportBtn?: () => void;
  exportIsLoading?: boolean;
  exportLoadingText?: string;
  searchPlaceholder?: string;
  controlledFilters?: Record<string, any>;
  controlledSearchString?: string;
  withRemoveFeature?: boolean;
  showHandleExportButton?: () => boolean;
  skipDayCheck?: boolean;
  /**
   * When set, the search input, the filters button, the secondary and the add-new button get
   * `${testIdPrefix}-search`, `-filter`, `-secondary` and `-new` as data-testid (ui-automation-tests).
   */
  testIdPrefix?: string;
  /**
   * Bump it to empty the search box and put the filter form back to `initialFilterState` (for a
   * "Clear search and filters" action outside the header). The parent clears its own applied state.
   */
  resetKey?: number;
}): JSX.Element {
  const getTestId = (suffix: string): string | undefined =>
    testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined;

  const confirmBtnRef = useRef<HTMLButtonElement>();
  const [filters, setFilters] = useState(initialFilterState);
  const [searchString, changeSearchString] = useState('');

  const [isVisibleRemoveApproval, setVisibleRemoveApproval] = useState<boolean>(false);

  useEffect(() => {
    if (controlledFilters) {
      setFilters(controlledFilters);
    }
  }, [controlledFilters]);

  useEffect(() => {
    if (typeof controlledSearchString === 'string') {
      changeSearchString(controlledSearchString);
    }
  }, [controlledSearchString]);

  useNonInitialEffect(() => {
    changeSearchString('');
    if (!controlledFilters) {
      setFilters(initialFilterState);
    }
  }, [resetKey]);

  useOutsideClick(confirmBtnRef, () => setVisibleRemoveApproval(false));

  const debounedSetSearchString = useCallback(
    debounce((value) => handleSearchValue(value), 1000),
    []
  );

  const areFiltersEnabled = useMemo(() => {
    if (appliedFilters == null) {
      return false;
    }
    return Object.values(appliedFilters).some(({ value }) => value.toString().length > 0);
  }, [appliedFilters]);

  const handleSearchChange = ({ target: { value } }) => {
    changeSearchString(value);
    debounedSetSearchString(value);
  };

  const handleClear = () => {
    changeSearchString('');
    handleSearchValue('');
  };

  const handleAddFilterInput = () =>
    setFilters((prevState) => {
      const keys = Object.keys(prevState);
      const lastKey = keys[keys.length - 1];
      const newKey = lastKey !== undefined ? Number(lastKey) + 1 : 1;
      const initialRawFilter =
        initialFilterState[newKey] ||
        initialFilterState['1'] ||
        initialFilterState['0'] ||
        Object.values(initialFilterState)[0];

      return { ...prevState, [newKey]: initialRawFilter };
    });

  const handleApplyFilters = () => {
    const filtersArray = Object.values(filters);

    if (filtersArray.some(({ value }) => value === '')) {
      return;
    }

    onApplyFilters(filtersArray);
  };

  const handleChangeFilterState = (filterRawKey, filterInputKey, value) => {
    setFilters((prevState) => {
      return {
        ...prevState,
        [filterRawKey]: {
          ...prevState[filterRawKey],
          [filterInputKey]: value,
        },
      };
    });
  };

  const createHandleDeleteFilterRaw = (dummy: number) => () => {
    setFilters((prevState) => {
      const newState = { ...prevState };
      delete newState[dummy];
      return newState;
    });
  };

  const handleRemove = () => {
    if (hasRemoveConfirmation) {
      setVisibleRemoveApproval(true);
    } else {
      handleRemoveItems();
    }
  };

  const renderRemoveButton = () => {
    if (isVisibleRemoveApproval) {
      return (
        <span ref={confirmBtnRef}>
          <Button leftIcon="trash" type="alert" onClick={handleRemoveItems}>
            Confirm
          </Button>
        </span>
      );
    }

    return (
      <Button leftIcon="trash" type="alert" onClick={handleRemove}>
        Remove
      </Button>
    );
  };

  return (
    <div className="page-header__actions-wrapper">
      {handleSearchValue ? (
        <Input
          className="header__search"
          isClearable
          icon="search"
          iconColor={searchString ? PRIMARY_COLOR : GREY_COLOR}
          value={searchString}
          onChange={handleSearchChange}
          onClear={handleClear}
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              e.stopPropagation();
            }
          }}
          placeholder={searchPlaceholder}
          data-testid={getTestId('search')}
        />
      ) : null}

      {selectedItems?.length > 0 ? (
        <>
          {moreActions?.length > 0 && <MoreActionsButton items={moreActions} />}

          {withRemoveFeature && renderRemoveButton()}
        </>
      ) : null}

      {showHandleExportButton() && handleExportBtn ? (
        <>
          <div className="page-header__separator" />
          <Button
            onClick={() => {
              if (!exportIsLoading) {
                handleExportBtn();
              }
            }}
            className="page-header__export-button"
            type="additional"
            size="m"
            isLoading={exportIsLoading}
            loadingText={exportLoadingText || 'Exporting...'}
            disabled={exportIsLoading}
          >
            {!exportIsLoading && <SVGIcon icon="download" color={PRIMARY_COLOR} />}
            <span>Export</span>
          </Button>
        </>
      ) : null}

      {onApplyFilters ? (
        <HeaderFiltersButtonWithTooltip
          testId={getTestId('filter')}
          clearTestId={testIdPrefix ? `${testIdPrefix}-filter-clear` : undefined}
          areFiltersEnabled={areFiltersEnabled}
          onClearAllFilters={() => {
            onApplyFilters([]);
            setFilters(initialFilterState);
          }}
        >
          <div className="campaign-filters-tooltip-content">
            <div className="campaign-filters-tooltip-content__body">
              {Object.entries(filters).map((filter, index) => {
                const filterId = Number(filter[0]);
                const filterRawData = filter[1];

                return (
                  <FiltersTooltipContentRaw
                    key={filterId}
                    rawId={filterId}
                    isAdditional={!!index}
                    onDeleteFilterInput={createHandleDeleteFilterRaw(filterId)}
                    filterRawData={filterRawData}
                    onChangeFilter={handleChangeFilterState}
                    filterFields={filterFields}
                    skipDayCheck={skipDayCheck}
                    testId={testIdPrefix ? `${testIdPrefix}-filter-row-${index}` : undefined}
                  />
                );
              })}
            </div>
            <FiltersTooltipContentFooter
              onAddFilterInput={handleAddFilterInput}
              onApplyFilters={handleApplyFilters}
              addTestId={testIdPrefix ? `${testIdPrefix}-filter-add` : undefined}
              applyTestId={testIdPrefix ? `${testIdPrefix}-filter-apply` : undefined}
            />
          </div>
        </HeaderFiltersButtonWithTooltip>
      ) : null}

      {secondaryBtn ? (
        <Button
          type="additional"
          size="m"
          isLoading={secondaryBtn.isLoading}
          loadingText={secondaryBtn.isLoadingText || ''}
          className="page-header__button"
          onClick={secondaryBtn.handleClick}
          data-testid={secondaryBtn.testId || getTestId('secondary')}
        >
          {secondaryBtn.icon && !secondaryBtn.isLoading ? (
            // Explicit color: icon defaults differ (UploadIcon is grey) and the "additional"
            // button expects a primary-blue icon that its hover filter turns white.
            <SVGIcon icon={secondaryBtn.icon} color={PRIMARY_COLOR} />
          ) : null}
          <span>{secondaryBtn.title}</span>
        </Button>
      ) : null}

      {addNewBtn ? (
        <Button
          isLoading={addNewBtn.isLoading}
          loadingText={addNewBtn.isLoadingText || ''}
          className="page-header__button"
          onClick={addNewBtn.handleClick}
          data-testid={addNewBtn.testId || getTestId('new')}
        >
          {addNewBtn.title}
        </Button>
      ) : null}
    </div>
  );
}

export default HeaderActions;
